import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { adminApi } from '@/lib/adminApi';
import { formatApiError } from '@/lib/api';

/**
 * UAT Sprint 01 · Findings 2 + 3.
 *
 * Edit an existing hm_advisor (name / phone / status). Uses the already-
 * shipped PATCH /admin/advisors/:id endpoint — no backend change.
 */
export default function EditAdvisorDialog({ open, onOpenChange, advisor, onUpdated }) {
  const { t } = useTranslation();
  const [form, setForm] = useState({ name: '', phone: '', status: 'active' });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open && advisor) {
      setForm({
        name: advisor.name || '',
        phone: advisor.phone || '',
        status: advisor.status || 'active',
      });
    }
  }, [open, advisor]);

  const save = async () => {
    if (!advisor) return;
    setSaving(true);
    try {
      await adminApi.patchAdvisor(advisor.id, {
        name: form.name.trim(),
        phone: form.phone.trim(),
        status: form.status,
      });
      toast.success(t('pages.advisors.updated', { defaultValue: 'Advisor updated' }));
      onUpdated?.();
      onOpenChange(false);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md" data-testid="edit-advisor-dialog">
        <DialogHeader>
          <DialogTitle>{t('pages.advisors.editTitle', { defaultValue: 'Edit advisor' })}</DialogTitle>
          <DialogDescription>{t('pages.advisors.editDesc', { defaultValue: "Update the advisor's basic information." })}</DialogDescription>
        </DialogHeader>
        <div className="grid gap-3 py-2">
          <div>
            <Label className="text-xs">{t('pages.advisors.nameLabel', { defaultValue: 'Full name' })}</Label>
            <Input
              value={form.name}
              onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
              data-testid="edit-advisor-name"
            />
          </div>
          <div>
            <Label className="text-xs">{t('pages.advisors.phoneLabel', { defaultValue: 'Phone' })}</Label>
            <Input
              value={form.phone}
              onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value }))}
              data-testid="edit-advisor-phone"
            />
          </div>
          <div>
            <Label className="text-xs">{t('pages.advisors.statusLabel', { defaultValue: 'Status' })}</Label>
            <Select value={form.status} onValueChange={(v) => setForm((f) => ({ ...f, status: v }))}>
              <SelectTrigger data-testid="edit-advisor-status"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="active">{t('pages.advisors.statusActive', { defaultValue: 'Active' })}</SelectItem>
                <SelectItem value="inactive">{t('pages.advisors.statusInactive', { defaultValue: 'Inactive' })}</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={saving} data-testid="edit-advisor-cancel">
            {t('common.cancel', { defaultValue: 'Cancel' })}
          </Button>
          <Button onClick={save} disabled={saving || !form.name.trim()} className="bg-gradient-primary hover:opacity-90" data-testid="edit-advisor-save">
            {saving ? <><Loader2 className="h-4 w-4 mr-1.5 animate-spin" />{t('common.saving', { defaultValue: 'Saving…' })}</> : t('common.save', { defaultValue: 'Save' })}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
